import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useCreateCategory,
  useCreateCollection,
  useUpdateCategory,
  useUpdateCollection,
} from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

const taxonomySchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional().nullable(),
  imageUrl: z.string().url("Must be a valid URL").optional().nullable().or(z.literal("")),
});

function TaxonomyFormDialog({ isOpen, onOpenChange, onSuccess, item, type }) {
  const { toast } = useToast();
  const createCategory = useCreateCategory();
  const createCollection = useCreateCollection();
  const updateCategory = useUpdateCategory();
  const updateCollection = useUpdateCollection();
  const isCollection = type === "collection";
  const label = isCollection ? "Collection" : "Category";
  const form = useForm({
    resolver: zodResolver(taxonomySchema),
    defaultValues: {
      name: item?.name || "",
      description: item?.description || "",
      imageUrl: item?.imageUrl || "",
    },
  });

  const onSubmit = async (data) => {
    try {
      const cleanData = { ...data, imageUrl: data.imageUrl || null };
      if (item) {
        const mutation = isCollection ? updateCollection : updateCategory;
        await mutation.mutateAsync({ id: item.id, data: cleanData });
      } else {
        const mutation = isCollection ? createCollection : createCategory;
        await mutation.mutateAsync(cleanData);
      }
      toast({ title: `${label} ${item ? "updated" : "created"} successfully.` });
      onSuccess();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = error.response?.status === 404 && isCollection
        ? "The configured backend does not have the collections endpoint yet. Deploy the latest backend version, then try again."
        : typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((issue) => issue.msg).join(", ")
            : error.message;
      toast({
        title: `Failed to save ${label.toLowerCase()}.`,
        description: message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none p-8" data-testid={`${type}-form-dialog`}>
        <DialogHeader className="mb-6">
          <DialogTitle className="font-serif text-2xl">{item ? `Edit ${label}` : `Create ${label}`}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel className="uppercase tracking-widest text-[10px]">{label} Name</FormLabel>
                <FormControl><Input {...field} className="rounded-none bg-muted/50 border-0 border-b-2 border-border focus-visible:border-foreground focus-visible:ring-0" /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="imageUrl" render={({ field }) => (
              <FormItem>
                <FormLabel className="uppercase tracking-widest text-[10px]">Home Image URL</FormLabel>
                <FormControl><Input {...field} value={field.value || ""} placeholder="https://..." className="rounded-none bg-muted/50 border-0 border-b-2 border-border focus-visible:border-foreground focus-visible:ring-0" /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel className="uppercase tracking-widest text-[10px]">Description</FormLabel>
                <FormControl><Textarea {...field} value={field.value || ""} rows={3} className="rounded-none bg-muted/50 border-0 border-b-2 border-border focus-visible:border-foreground focus-visible:ring-0 resize-none" /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <div className="flex justify-end gap-4 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none uppercase tracking-widest text-xs">Cancel</Button>
              <Button type="submit" disabled={form.formState.isSubmitting} className="rounded-none uppercase tracking-widest text-xs">
                {form.formState.isSubmitting ? "Saving..." : item ? "Save Changes" : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export function CategoryFormDialog(props) {
  return <TaxonomyFormDialog {...props} type="category" />;
}

export function CollectionFormDialog(props) {
  return <TaxonomyFormDialog {...props} type="collection" />;
}
